// "Instant payouts are here" (founder pick B, 1.5.2): the one-time sheet on
// the Balance tab for talent who can get instant payouts but have no debit
// card yet. Who sees it is decided ONLY from this visit's server answers —
// GET /payments/instant-payouts and the talent's own profile — never from the
// remembered copies the tab paints with (see the money note in lib/pageCache):
// a remembered "needs a card" from last week must not pop a sheet at someone
// who has added one since.
//
// Once per account, on this device: the flag is set the moment the sheet
// opens, so "Not now", closing it, adding a card — or the app being closed
// with it up — all mean never again. Kept in localStorage per account id,
// like utils/desktopPromptSeen. If the founder later wants "once" to hold
// across devices, this flag moves to the server (a field on the talent's
// profile) and this file is the one place that changes.
import type { InstantPayoutStatus } from "../types/payments";
import type { User } from "../types/User";
import { masspayVerified } from "../lib/connectBank";
import { takesMoney } from "./takesMoney";

const KEY_PREFIX = "rq_instant_intro_seen_";

export function hasSeenInstantIntro(accountId: string): boolean {
  try {
    return localStorage.getItem(KEY_PREFIX + accountId) === "1";
  } catch {
    // Storage unreadable (private mode): treat as seen — a nudge that could
    // repeat on every visit is worse than one that never shows.
    return true;
  }
}

export function markInstantIntroSeen(accountId: string): void {
  try {
    localStorage.setItem(KEY_PREFIX + accountId, "1");
  } catch {
    /* quota / private mode — hasSeenInstantIntro already answers "seen" there */
  }
}

/**
 * Should the sheet open now? `status` and `profile` are THIS visit's answers
 * (null until they land — never the painted cache).
 */
export function shouldShowInstantIntro({
  status,
  profile,
  seen,
}: {
  status: InstantPayoutStatus | null;
  profile: Partial<User> | null;
  seen: boolean;
}): boolean {
  if (seen || !status || !profile) return false;
  // Clears every instant-payout rule; the debit card is the only thing missing.
  if (!status.rulesPass || !status.needsCard || status.eligible) return false;
  // Paid through Stripe only: never the free tier (no bank / not approved),
  // never a MassPay talent (no debit-card payouts there).
  if (!takesMoney(profile)) return false;
  if (profile.payout?.rail === "masspay" || masspayVerified(profile)) return false;
  if (profile.isStripeOnboarded !== true) return false;
  return true;
}
